import { HttpClientType } from 'api/axios';
import { useFetchExColumnsApi } from 'api/shared/ex_columns';
import { ExColumn } from 'entities/ex_column';
import { ProofreadDefaultFilterItem, ProofreadPlanForm } from 'entities/proofread_plan';
import { Form } from 'rac';
import React, { useEffect, useMemo, useState } from 'react';

export type FilterRow = {
  exColumnId: string;
  value: string;
};

export type ProofreadPlanFilterSet = {
  proofreadDropdownExColumns: ExColumn[];
  filterConditions: FilterRow[][];
  resetFilterConditions: () => void;
  handleAddCondition: () => void;
  handleRemoveCondition: (conditionIndex: number) => void;
  handleAddFilter: (conditionIndex: number) => void;
  handleRemoveFilter: (conditionIndex: number, filterIndex: number) => void;
  handleExColumnChange: (conditionIndex: number, filterIndex: number, exColumnId: string) => void;
  handleValueChange: (conditionIndex: number, filterIndex: number, value: string) => void;
};

export const useProofreadPlanInstrumentFilter = (
  form: Form<ProofreadPlanForm>,
  clientType: HttpClientType
): ProofreadPlanFilterSet => {
  const exColumnsApi = useFetchExColumnsApi(clientType);
  const initialFilterConditions: FilterRow[][] = [[{ exColumnId: '', value: '' }]];
  const [filterConditions, setFilterConditions] = useState<FilterRow[][]>(initialFilterConditions);

  const resetFilterConditions = () => {
    setFilterConditions([[{ exColumnId: '', value: '' }]]);
  };

  useEffect(() => {
    exColumnsApi.execute();
  }, []);

  const proofreadDropdownExColumns: ExColumn[] = useMemo(
    () =>
      (exColumnsApi.response.exColumns || []).filter(
        (col) => col.extensionClass === 'ProofreadEvent' && col.inputType === 'dropdown'
      ),
    [exColumnsApi.response.exColumns]
  );

  // ローカルのフィルタ状態をフォームに同期
  useEffect(() => {
    const items: ProofreadDefaultFilterItem[] = [];
    filterConditions.forEach((condition, patternIndex) => {
      condition.forEach((row) => {
        if (row.exColumnId && row.value) {
          items.push({ patternIndex, exColumnId: Number(row.exColumnId), value: row.value });
        }
      });
    });
    form.update((f) => {
      f.proofreadDefaultFilterPatterns = items;
    });
  }, [filterConditions]);

  const handleAddCondition = () => {
    setFilterConditions((prev) => [...prev, [{ exColumnId: '', value: '' }]]);
  };

  const handleRemoveCondition = (conditionIndex: number) => {
    setFilterConditions((prev) => prev.filter((_, i) => i !== conditionIndex));
  };

  const handleAddFilter = (conditionIndex: number) => {
    setFilterConditions((prev) => {
      const next = [...prev];
      next[conditionIndex] = [...next[conditionIndex], { exColumnId: '', value: '' }];
      return next;
    });
  };

  const handleRemoveFilter = (conditionIndex: number, filterIndex: number) => {
    setFilterConditions((prev) => {
      const next = [...prev];
      const remaining = next[conditionIndex].filter((_, i) => i !== filterIndex);
      if (remaining.length === 0) {
        // 条件内の項目が全て消えたら条件ごと削除
        const withoutCondition = next.filter((_, i) => i !== conditionIndex);
        // 全条件がなくなったら初期状態に戻す
        return withoutCondition.length > 0 ? withoutCondition : [[{ exColumnId: '', value: '' }]];
      }
      next[conditionIndex] = remaining;
      return next;
    });
  };

  const handleExColumnChange = (conditionIndex: number, filterIndex: number, exColumnId: string) => {
    setFilterConditions((prev) => {
      const next = [...prev];
      next[conditionIndex] = [...next[conditionIndex]];
      next[conditionIndex][filterIndex] = { exColumnId, value: '' };
      return next;
    });
  };

  const handleValueChange = (conditionIndex: number, filterIndex: number, value: string) => {
    setFilterConditions((prev) => {
      const next = [...prev];
      next[conditionIndex] = [...next[conditionIndex]];
      next[conditionIndex][filterIndex] = { ...next[conditionIndex][filterIndex], value };
      return next;
    });
  };

  return {
    proofreadDropdownExColumns,
    filterConditions,
    resetFilterConditions,
    handleAddCondition,
    handleRemoveCondition,
    handleAddFilter,
    handleRemoveFilter,
    handleExColumnChange,
    handleValueChange,
  };
};
